import { forwardRef } from "react";
import { Box } from "@mui/material";
import { useControlled } from "@mui/material/utils";

import {
  AccordionGroupContext,
  type AccordionVariant,
} from "../Accordion/AccordionGroupContext";
import { tokens } from "../../tokens/generated/tokens";

import type { SxProps, Theme } from "@mui/material/styles";
import type {
  ComponentPropsWithoutRef,
  ReactNode,
  SyntheticEvent,
} from "react";

/** Same scoping as `Alert`: dark values apply unless a nearer scope pins light. */
const DARK_SCHEME =
  '[data-mui-color-scheme="dark"] &:not([data-mui-color-scheme="light"] *)';

const { borderRadius, borderWidth } = tokens.functional.size;

const NONE_EXPANDED: readonly string[] = [];

export interface AccordionGroupProps extends Omit<
  ComponentPropsWithoutRef<"div">,
  "onChange"
> {
  /** `Accordion` elements, each with a `value`. The group sets their `grouped` and `variant`. */
  children: ReactNode;
  /** Group treatment, forwarded to every child. Default: "contained". */
  variant?: AccordionVariant;
  /** Allows only one child open at a time; given several `expanded` values, only the first opens. Default: false. */
  exclusive?: boolean;
  /** Controlled: the `value`s of the open children. */
  expanded?: readonly string[];
  /** Uncontrolled: the `value`s open on first render. Default: none. */
  defaultExpanded?: readonly string[];
  /** Fires when a child opens or closes, with the `value`s now open. */
  onChange?: (event: SyntheticEvent, expanded: readonly string[]) => void;
  /** Root test id. Default: "ds-accordion-group". */
  "data-testid"?: string;
}

const borderColorByScheme = (scheme: keyof typeof tokens.theme): string =>
  tokens.theme[scheme].borderColor.default;

/**
 * `contained` owns the border and radius its children give up, and clips them
 * so the first and last child take the rounded corners without drawing their
 * own. `plain` holds the stack together by proximity alone.
 */
const rootSxByVariant: Record<AccordionVariant, SxProps<Theme>> = {
  contained: {
    border: `${borderWidth.default}px solid ${borderColorByScheme("light")}`,
    borderRadius: `${borderRadius.default}px`,
    overflow: "hidden",
    [DARK_SCHEME]: { borderColor: borderColorByScheme("dark") },
  },
  plain: {},
};

const nextExpanded = (
  current: readonly string[],
  value: string,
  isExpanded: boolean,
  exclusive: boolean,
): readonly string[] => {
  if (!isExpanded) return current.filter((open) => open !== value);
  if (exclusive) return [value];
  return [...current, value];
};

/** Stacks `Accordion` children into one object and owns which of them are open. */
export const AccordionGroup = forwardRef<HTMLDivElement, AccordionGroupProps>(
  (props, ref) => {
    const {
      children,
      variant = "contained",
      exclusive = false,
      expanded: expandedProp,
      defaultExpanded = NONE_EXPANDED,
      onChange,
      "data-testid": dataTestId = "ds-accordion-group",
      ...rest
    } = props;

    const [expanded, setExpanded] = useControlled({
      controlled: expandedProp,
      default: defaultExpanded,
      name: "AccordionGroup",
      state: "expanded",
    });
    // A caller can pass several values with `exclusive` on; only the first opens.
    const openValues = exclusive ? expanded.slice(0, 1) : expanded;

    const toggle = (
      event: SyntheticEvent,
      value: string,
      isExpanded: boolean,
    ): void => {
      const next = nextExpanded(openValues, value, isExpanded, exclusive);
      setExpanded(next);
      onChange?.(event, next);
    };

    return (
      <AccordionGroupContext.Provider
        value={{
          variant,
          isExpanded: (value) => openValues.includes(value),
          toggle,
        }}
      >
        <Box
          ref={ref}
          data-testid={dataTestId}
          sx={rootSxByVariant[variant]}
          {...rest}
        >
          {children}
        </Box>
      </AccordionGroupContext.Provider>
    );
  },
);

AccordionGroup.displayName = "AccordionGroup";
